import { ApiHelper } from '@nykaa/utils/network';
import { ROUTES, getUrl } from '@nykaa/utils/network/urls';
import { queryStringFromParams } from 'shared/helpers/utils/url';
import {
  platformNames,
  DYNAMIC_FOOTER_LINK_API_ERR_MESSAGE,
} from 'shared/constants';
import { AdditionalRequestOptions } from '@nykaa/utils/network/types';

import {
  transformProductData,
  transformDynamicFooterLinks,
  transformProductOffersV2,
} from './transformer';
import { OffersParams, FetchProductParams } from './types';
import { logger } from '@nykaa/logger';
import { HttpMethods } from 'constants/requestHeaders';

const mandatoryRequestParams = {
  app_version: '2.0.6',
  source: 'react',
  variants: true,
};

export const getProduct = async ({
  id,
  skuId,
  store,
  pro,
  enablePR,
  priveBand,
  regionId,
  internalSlug,
}: FetchProductParams) => {
  const url = getUrl(ROUTES.PRODUCT);
  const isMen = __PLATFORM__ === platformNames.MEN;
  const queryParams = {
    ...mandatoryRequestParams,
    product_id: id,
    ...(pro && { pro }),
    ...(store && { store }), // Optionally add store if it exists
    ...(enablePR && { enable_pr: '1' }),
    ...(priveBand && { customer_tier: isMen ? 'non_prive' : priveBand }),
    ...(regionId && {
      region_id: regionId,
      internal_slug: internalSlug,
    }),
  };

  const queryString = queryStringFromParams(queryParams);

  try {
    const {
      data: { response },
    } = await ApiHelper(`${url}${queryString}`, 'get');

    if (!response) {
      throw new Error('"response" not found in api -> data');
    }
    return transformProductData(response, skuId);
  } catch (e) {
    // * https://github.com/axios/axios/issues/472#issuecomment-252584718
    logger.error(e, `Error fetching product ${url}${queryString}`, {
      apiUrl: `${url}${queryString}`,
    });
    throw e;
  }
};

export const getProductOffers = async (
  params: OffersParams,
  isOfferApiV2Enabled?: boolean
) => {
  const productId = params.productId;
  let domainParams = {};
  if (__PLATFORM__ === platformNames.MEN) {
    domainParams = { domain: __PLATFORM__ };
  }
  const queryParams = {
    ...params,
    ...mandatoryRequestParams,
    ...domainParams,
    productId,
  };
  const url = isOfferApiV2Enabled
    ? getUrl(ROUTES.COUPONS_OFFERS_V2)
    : getUrl(ROUTES.COUPONS_OFFERS);
  const queryString = queryStringFromParams({ ...queryParams });
  const optionals: AdditionalRequestOptions = { CATALOG_TAG_FILTER: false };
  try {
    const { data: response } = await ApiHelper(
      `${url}${queryString}`,
      'get',
      {},
      {},
      optionals
    );
    const { data } = response || {};
    return transformProductOffersV2(data?.offers);
  } catch (e) {
    logger.log({ error: e, message: 'Error fetching offers' });
    throw e;
  }
};

export const getDynamicFooterLinks = async (params: any = {}) => {
  const url = '/gateway-api/api/v1/products/get_dynamic_footer_links';
  const { l1Id, l2Id, l3Id } = params;
  let queryParams = {
    domain: __PLATFORM__,
    l1_id: l1Id,
    l2_id: l2Id,
    l3_id: l3Id,
  };
  const queryString = queryStringFromParams(queryParams);
  try {
    const {
      data: { response },
    } = await ApiHelper(`${url}${queryString}`, HttpMethods.GET);

    return transformDynamicFooterLinks(response);
  } catch (e) {
    logger.error(e, `${DYNAMIC_FOOTER_LINK_API_ERR_MESSAGE} :url=${url}`);
    throw e;
  }
};
